import React, { useContext, useEffect, useId, useState } from "react";

import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  CampaignOutlined,
  CloseRounded,
  ChevronRightRounded,
  HelpOutlineRounded,
  InfoOutlined,
  MenuBookOutlined,
  LockRounded,
} from "@mui/icons-material";

import { ajustarTamanhoFonteAdmin } from "../estilos/global";
import { useMovimentoReduzido } from "../contextos/PreferenciasContext";
import { GUIA_UTILIZACAO_URL } from "../config/ajuda";
import {
  estilosMenuAjuda as estilosAjuda,
  painelMenuAjuda,
} from "../estilos/ajuda";

import NovidadesDialog from "./NovidadesDialog";
import { CondicaoContext } from "../contexto";
import { novidades, novidadeVisivelPara } from "../config/novidades";
import { chaveLeituraNovidades } from "./novidades/leituraNovidades";
import { useNovidadesVistas } from "./novidades/useNovidadesVistas";
import { destaqueAcaoNovidades } from "./novidades/estilosNovidades";
import SobreSearDialog from "./SobreSearDialog";

const NOVIDADES_EM_DESENVOLVIMENTO = false;

// Texto reutilizado nas opções.
function TextoAjuda({ titulo }) {
  return (
    <Box sx={estilosAjuda.textoLinha}>
      <Typography
        component="span"
        sx={{
          display: "block",
          ...estilosAjuda.tituloLinha,
        }}
      >
        {titulo}
      </Typography>
    </Box>
  );
}

export default function AjudaMenu({ expandido, onOpenChange }) {
  const { userId, role } = useContext(CondicaoContext);
  const [vistas] = useNovidadesVistas(chaveLeituraNovidades(userId, role));
  const temNovidades = !NOVIDADES_EM_DESENVOLVIMENTO && novidades.some(item => novidadeVisivelPara(item, role) && !vistas.includes(item.id));
  const [anchorEl, setAnchorEl] = useState(null);
  const movimentoReduzido = useMovimentoReduzido();

  const [novidadesAbertas, setNovidadesAbertas] = useState(false);
  const [sobreAberto, setSobreAberto] = useState(false);

  const id = useId();

  const idBotao = `ajuda-botao-${id}`;
  const idMenu = `ajuda-menu-${id}`;

  const aberto = Boolean(anchorEl);
  const destacarAjuda = temNovidades && !aberto && !novidadesAbertas && !sobreAberto;

  const fechar = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    onOpenChange?.(aberto || novidadesAbertas || sobreAberto);

    return () => onOpenChange?.(false);
  }, [aberto, novidadesAbertas, sobreAberto, onOpenChange]);

  const abrirNovidades = () => {
    fechar();
    setSobreAberto(false);
    setNovidadesAbertas(true);
  };

  const abrirSobre = () => {
    fechar();
    setSobreAberto(true);
  };

  return (
    <>
      {/* BOTÃO DE AJUDA NO MENU LATERAL */}
      <ListItemButton
        id={idBotao}
        component="button"
        type="button"
        aria-label="Abrir Ajuda"
        aria-controls={aberto ? idMenu : undefined}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          ...(expandido && destacarAjuda && destaqueAcaoNovidades),
          minHeight: 48,
          width: expandido ? "100%" : 56,
          px: 0.5,

          justifyContent: expandido ? "flex-start" : "center",
          borderRadius: 2,

          color: "var(--sear-azul, #075bb7)",
          overflow: "hidden",
          textAlign: "left",

          "&:hover": {
            bgcolor: expandido
              ? "var(--sear-superficie-hover, #edf5ff)"
              : "transparent",
          },
        }}
      >
        <ListItemIcon
          sx={{
            ...(!expandido && destacarAjuda && destaqueAcaoNovidades),
            minWidth: 0,
            width: 40,
            height: 40,
            flexShrink: 0,
            mr: expandido ? 1.5 : 0,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            bgcolor: "var(--sear-superficie-azul, #eaf4ff)",
            borderRadius: 1.5,
            color: "var(--sear-azul, #087cf0)",
          }}
        >
          <HelpOutlineRounded sx={{ fontSize: 24 }} />
        </ListItemIcon>

        {expandido && (
          <ListItemText
            primary="Ajuda"
            sx={{
              m: 0,
              minWidth: 0,
            }}
            primaryTypographyProps={{
              noWrap: true,
              sx: {
                fontSize: ajustarTamanhoFonteAdmin("0.9rem"),
                fontWeight: 600,
              },
            }}
          />
        )}
      </ListItemButton>

      {/* PAINEL DE AJUDA */}
      <Popover
        id={idMenu}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: -12, horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        marginThreshold={12}
        open={aberto}
        onClose={fechar}
        transitionDuration={movimentoReduzido ? 0 : 180}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": "Ajuda",
            sx: painelMenuAjuda,
          },
        }}
      >
        <Box sx={{ p: 1.25 }}>
          {/* CABEÇALHO */}
          <Box sx={estilosAjuda.cabecalho}>
            <Box sx={estilosAjuda.iconeCabecalho}>
              <HelpOutlineRounded sx={{ fontSize: 22 }} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography component="h2" sx={estilosAjuda.tituloCabecalho}>
                Ajuda
              </Typography>
            </Box>
            <IconButton
              autoFocus
              aria-label="Fechar Ajuda"
              onClick={fechar}
              sx={estilosAjuda.fechar}
            >
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Divider sx={estilosAjuda.divisor} />

          {/* GUIA DO USUÁRIO */}
          <ButtonBase
            component="a"
            href={GUIA_UTILIZACAO_URL}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!GUIA_UTILIZACAO_URL}
            onClick={fechar}
            aria-label="Guia do Usuário (abre em nova aba)"
            sx={estilosAjuda.linha}
          >
            <ListItemIcon sx={estilosAjuda.iconeLinha}>
              <MenuBookOutlined />
            </ListItemIcon>

            <TextoAjuda titulo="Guia do Usuário" />

            <ChevronRightRounded sx={estilosAjuda.seta} />
          </ButtonBase>

          {/* NOVIDADES DO SEAR */}
          <Tooltip
            title={NOVIDADES_EM_DESENVOLVIMENTO ? "Em desenvolvimento" : ""}
            placement="right"
            arrow
            enterDelay={250}
          >
            <ButtonBase
              aria-label={
                NOVIDADES_EM_DESENVOLVIMENTO
                  ? "Novidades do SEAR em desenvolvimento"
                  : "Novidades do SEAR"
              }
              aria-disabled={NOVIDADES_EM_DESENVOLVIMENTO || undefined}
              disableRipple={NOVIDADES_EM_DESENVOLVIMENTO}
              onClick={
                NOVIDADES_EM_DESENVOLVIMENTO ? undefined : abrirNovidades
              }
              sx={{
                ...estilosAjuda.linha,
                ...(temNovidades && destaqueAcaoNovidades),
                ...(NOVIDADES_EM_DESENVOLVIMENTO && {
                  cursor: "not-allowed",
                  "&:hover": {
                    bgcolor: "transparent",
                  },
                  "& .MuiTypography-root": {
                    color: "text.disabled",
                  },
                }),
              }}
            >
              <ListItemIcon
                sx={{
                  ...estilosAjuda.iconeLinha,
                  ...(NOVIDADES_EM_DESENVOLVIMENTO && {
                    bgcolor: "action.disabledBackground",
                    color: "text.disabled",
                  }),
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                  }}
                >
                  <CampaignOutlined />

                  {NOVIDADES_EM_DESENVOLVIMENTO && (
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: "absolute",
                        right: -5,
                        bottom: -5,
                        width: 16,
                        height: 16,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        bgcolor: "background.paper",
                        "& .MuiSvgIcon-root": {
                          fontSize: 12,
                        },
                      }}
                    >
                      <LockRounded />
                    </Box>
                  )}
                </Box>
              </ListItemIcon>

              <TextoAjuda titulo="Novidades do SEAR" />

              {!NOVIDADES_EM_DESENVOLVIMENTO && (
                <ChevronRightRounded sx={estilosAjuda.seta} />
              )}
            </ButtonBase>
          </Tooltip>

          {/* SOBRE O SEAR */}
          <ButtonBase
            aria-label="Sobre o SEAR"
            onClick={abrirSobre}
            sx={estilosAjuda.linha}
          >
            <ListItemIcon sx={estilosAjuda.iconeLinha}>
              <InfoOutlined />
            </ListItemIcon>

            <TextoAjuda titulo="Sobre o SEAR" />

            <ChevronRightRounded sx={estilosAjuda.seta} />
          </ButtonBase>
        </Box>
      </Popover>

      {/* JANELAS EXISTENTES */}
      <NovidadesDialog
        open={novidadesAbertas}
        onClose={() => setNovidadesAbertas(false)}
      />

      <SobreSearDialog
        open={sobreAberto}
        onClose={() => setSobreAberto(false)}
        onAbrirNovidades={abrirNovidades}
        novidadesEmDesenvolvimento={NOVIDADES_EM_DESENVOLVIMENTO}
      />
    </>
  );
}
